import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Building2, X, Send, ShieldCheck, ArrowUpRight, UserPlus, Check, Search, MoreVertical, KeyRound, MailPlus, UserX, Trash2, Pencil } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function ContractBadge({ org }) {
  const cs = org.contract_status || (org.demo_mode ? "demo" : "paid");
  if (cs === "demo") return <Badge className="bg-amber-100 text-amber-700 border-0 text-xs font-semibold">DEMO</Badge>;
  if (cs === "paid") return <Badge className="bg-lime-100 text-lime-700 border-0 text-xs font-semibold">PAID</Badge>;
  return <Badge className="bg-slate-100 text-slate-500 border-0 text-xs font-semibold">{cs.toUpperCase()}</Badge>;
}

function TagInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const handleKey = (e) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      if (!values.includes(input.trim())) onChange([...values, input.trim()]);
      setInput("");
    }
  };
  return (
    <div className="border rounded-lg p-2 bg-white min-h-[42px] flex flex-wrap gap-1.5 items-center">
      {values.map(v => (
        <span key={v} className="bg-slate-100 text-slate-700 rounded-full px-3 py-1 text-sm flex items-center gap-1">
          {v}<button type="button" onClick={() => onChange(values.filter(x => x !== v))} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} placeholder={values.length === 0 ? placeholder : ""} className="flex-1 min-w-[120px] outline-none text-sm border-0 p-1" />
    </div>
  );
}

function DemoInviteModal({ api, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", company_name: "", personal_note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!form.email || !form.full_name || !form.company_name) { toast.error("Email, name, and company required"); return; }
    setSending(true);
    try {
      await api.post("/admin/demo-invite", form);
      setSent(true);
      onSuccess?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
    finally { setSending(false); }
  };

  const reset = () => { setForm({ email: "", full_name: "", company_name: "", personal_note: "" }); setSent(false); };

  return (
    <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="demo-invite-btn" className="bg-lime-600 hover:bg-lime-700 text-white font-accent"><Send className="w-4 h-4 mr-1" /> Invite to Demo</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Invite to Demo</DialogTitle></DialogHeader>
        {sent ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-lime-100 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-lime-600" /></div>
            <p className="text-sm text-slate-600">Demo invitation sent to <strong>{form.email}</strong>.</p>
            <p className="text-xs text-slate-400 mt-2">They will receive an email with access instructions.</p>
            <Button variant="outline" className="mt-4" onClick={() => { setOpen(false); reset(); }}>Close</Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Prospect Email</Label><Input data-testid="demo-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="name@company.com" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Full Name</Label><Input data-testid="demo-name" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Jane Smith" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Company / Organization</Label><Input data-testid="demo-company" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Novo Nordisk" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Personal Note (Optional)</Label><Textarea data-testid="demo-note" value={form.personal_note} onChange={e => setForm(p => ({ ...p, personal_note: e.target.value }))} placeholder="Add a personal note to your invitation..." className="min-h-[80px]" /></div>
            <Button data-testid="send-demo-invite-btn" onClick={handleSend} disabled={sending} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white">{sending ? "Sending..." : "Send Demo Invite"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UpgradeModal({ org, api, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleUpgrade = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/clients/${org.id}/upgrade`);
      toast.success(`${org.name} upgraded to Paid`);
      setOpen(false);
      onSuccess?.();
    } catch { toast.error("Upgrade failed"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-lime-300 text-lime-700 hover:bg-lime-50" data-testid={`upgrade-btn-${org.id}`}><ArrowUpRight className="w-3 h-3 mr-1" /> Upgrade to Paid</Button>
      </DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle className="font-heading">Upgrade {org.name} to Paid?</DialogTitle></DialogHeader>
        <div className="py-4 space-y-3 text-sm text-slate-600">
          <p>This will:</p>
          <div className="space-y-2 pl-2">
            <p className="flex items-center gap-2"><Check className="w-4 h-4 text-lime-600" /> Enable real Work Order fulfillment</p>
            <p className="flex items-center gap-2"><Check className="w-4 h-4 text-lime-600" /> Remove the Demo Mode banner for their users</p>
            <p className="flex items-center gap-2"><Check className="w-4 h-4 text-lime-600" /> Allow Work Orders to trigger admin notifications</p>
          </div>
          <p className="text-xs text-slate-400 mt-4">This action is logged and cannot be automatically reversed.</p>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleUpgrade} disabled={loading} className="bg-evergreen-950 hover:bg-evergreen-900 text-white" data-testid="confirm-upgrade-btn">{loading ? "Upgrading..." : "Confirm Upgrade"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientManagement() {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", suppression_names: "", suppression_domains: "", admin_email: "" });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  // Suppression editor state
  const [supNames, setSupNames] = useState([]);
  const [supDomains, setSupDomains] = useState([]);
  const [supTest, setSupTest] = useState("");
  const [supTestResult, setSupTestResult] = useState(null);
  const [savingSup, setSavingSup] = useState(false);
  // Delegate state
  const [promoteUserId, setPromoteUserId] = useState("");

  const loadClients = () => api.get("/admin/clients").then(r => setClients(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { loadClients(); }, [api]); // eslint-disable-line

  const loadClientDetail = async (orgId) => {
    try {
      const res = await api.get(`/admin/clients/${orgId}`);
      setClientDetail(res.data);
      setSelectedClient(orgId);
      setSupNames(res.data.organization?.suppression_names || []);
      setSupDomains(res.data.organization?.suppression_domains || []);
      setSupTestResult(null);
    } catch { toast.error("Failed to load client"); }
  };

  const handleCreate = async () => {
    if (!newClient.name || !newClient.admin_email) { toast.error("Name and admin email required"); return; }
    setCreating(true);
    try {
      const payload = { name: newClient.name, suppression_names: newClient.suppression_names.split(",").map(s => s.trim()).filter(Boolean), suppression_domains: newClient.suppression_domains.split(",").map(s => s.trim()).filter(Boolean), admin_email: newClient.admin_email };
      const res = await api.post("/admin/clients", payload);
      toast.success(`Client created`);
      navigator.clipboard.writeText(window.location.origin + res.data.admin_invite_url);
      toast.info("Invite link copied to clipboard");
      setShowNew(false); setNewClient({ name: "", suppression_names: "", suppression_domains: "", admin_email: "" });
      loadClients();
    } catch (e) { toast.error("Creation failed"); }
    finally { setCreating(false); }
  };

  const handleSaveSuppression = async () => {
    setSavingSup(true);
    try {
      await api.put(`/admin/clients/${selectedClient}`, { suppression_names: supNames, suppression_domains: supDomains });
      toast.success("Suppression profile saved");
      loadClientDetail(selectedClient);
    } catch { toast.error("Save failed"); }
    finally { setSavingSup(false); }
  };

  const handleTestSuppression = async () => {
    if (!supTest.trim()) return;
    try {
      const res = await api.post(`/admin/clients/${selectedClient}/test-suppression`, { test_value: supTest });
      setSupTestResult(res.data);
    } catch { toast.error("Test failed"); }
  };

  const handlePromote = async (userId) => {
    try { await api.put(`/admin/clients/${selectedClient}/delegates/${userId}`); toast.success("Promoted to delegate"); loadClientDetail(selectedClient); } catch { toast.error("Failed"); }
  };
  const handleDemote = async (userId) => {
    try { await api.delete(`/admin/clients/${selectedClient}/delegates/${userId}`); toast.success("Removed as delegate"); loadClientDetail(selectedClient); } catch { toast.error("Failed"); }
  };

  const handleDowngrade = async (orgId) => {
    try { await api.post(`/admin/clients/${orgId}/downgrade`); toast.success("Downgraded to demo"); loadClients(); if (selectedClient === orgId) loadClientDetail(orgId); } catch { toast.error("Failed"); }
  };

  // ─── Detail View ───
  if (selectedClient && clientDetail) {
    const org = clientDetail.organization;
    const isDemoOrg = org.contract_status === "demo" || org.demo_mode;
    const delegates = (clientDetail.users || []).filter(u => u.role === "client_admin");
    const members = (clientDetail.users || []).filter(u => u.role === "client_member");
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-detail">
        <Button variant="ghost" onClick={() => { setSelectedClient(null); setClientDetail(null); }} className="mb-4 text-slate-500">&larr; Back to Clients</Button>
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <h1 className="text-2xl font-heading text-slate-900">{org.name}</h1>
          <ContractBadge org={org} />
          <StatusBadge status={org.status} />
          {isDemoOrg ? <UpgradeModal org={org} api={api} onSuccess={() => { loadClients(); loadClientDetail(selectedClient); }} /> :
            <Button variant="ghost" size="sm" className="text-xs text-slate-400" onClick={() => handleDowngrade(org.id)}>Revert to Demo</Button>}
        </div>
        {/* Master Switch */}
        <Card className="mb-6 rounded-xl border"><CardContent className="p-4 flex items-center gap-4">
          <ContractBadge org={org} />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">{isDemoOrg ? "Demo Mode Active" : "Full Platform Access"}</p>
            <p className="text-xs text-slate-400">{isDemoOrg ? "Work Orders are flagged demo — no real outreach triggered" : "Full platform access — Work Orders trigger real fulfillment"}</p>
          </div>
        </CardContent></Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Suppression Editor */}
          <Card className="lg:col-span-2"><CardContent className="p-6">
            <h3 className="font-heading text-slate-900 mb-4">Suppression Profile</h3>
            <div className="mb-4">
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Company Names</Label>
              <TagInput values={supNames} onChange={setSupNames} placeholder="Type name, press Enter..." />
            </div>
            <div className="mb-4">
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Email Domains</Label>
              <TagInput values={supDomains} onChange={setSupDomains} placeholder="Type domain, press Enter..." />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Input value={supTest} onChange={e => { setSupTest(e.target.value); setSupTestResult(null); }} placeholder="Test: enter company name or domain..." className="flex-1" data-testid="suppression-test-input" />
              <Button variant="outline" size="sm" onClick={handleTestSuppression} data-testid="test-suppression-btn"><Search className="w-4 h-4 mr-1" /> Test</Button>
            </div>
            {supTestResult && (
              <div className={`p-3 rounded-lg text-sm mb-4 ${supTestResult.suppressed ? "bg-red-50 text-red-700 border border-red-200" : "bg-lime-50 text-lime-700 border border-lime-200"}`} data-testid="suppression-test-result">
                {supTestResult.suppressed ? `Would be suppressed (matched ${supTestResult.matched_by}: "${supTestResult.matched_value}")` : "Would NOT be suppressed"}
              </div>
            )}
            <Button onClick={handleSaveSuppression} disabled={savingSup} className="bg-evergreen-950 hover:bg-evergreen-900 text-white" data-testid="save-suppression-btn">{savingSup ? "Saving..." : "Save Suppression Profile"}</Button>
          </CardContent></Card>

          {/* Delegates */}
          <Card><CardContent className="p-6">
            <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4">Organization Delegates</h3>
            <div className="space-y-2 mb-4">
              {delegates.map(u => (
                <div key={u.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <div><p className="text-sm font-medium text-slate-900">{u.full_name}</p><p className="text-xs text-slate-500">{u.email}</p></div>
                  <Button variant="ghost" size="sm" className="text-xs text-red-500 hover:text-red-700" onClick={() => handleDemote(u.id)}>Remove</Button>
                </div>
              ))}
              {delegates.length === 0 && <p className="text-xs text-slate-400">No delegates</p>}
            </div>
            {members.length > 0 && (
              <div className="flex gap-2">
                <Select value={promoteUserId} onValueChange={setPromoteUserId}>
                  <SelectTrigger className="flex-1 text-sm"><SelectValue placeholder="Select member..." /></SelectTrigger>
                  <SelectContent>{members.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" disabled={!promoteUserId} onClick={() => { handlePromote(promoteUserId); setPromoteUserId(""); }} data-testid="promote-delegate-btn"><UserPlus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
            )}
          </CardContent></Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="lg:col-span-2"><CardContent className="p-6">
            <h3 className="font-heading text-slate-900 mb-4">All Users ({clientDetail.users?.length})</h3>
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-slate-400">User</th>
                  <th className="px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-slate-400">Role</th>
                  <th className="px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-slate-400">Status</th>
                  <th className="px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-slate-400">Joined</th>
                  <th className="px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-slate-400 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {clientDetail.users?.map(u => (
                    <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{u.full_name || u.email.split("@")[0]}</p>
                        <p className="text-xs text-slate-400 font-mono">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={u.role === "client_admin" ? "bg-blue-100 text-blue-700 border-0 text-[10px]" : u.role === "master_admin" ? "bg-evergreen-100 text-evergreen-700 border-0 text-[10px]" : "bg-slate-100 text-slate-600 border-0 text-[10px]"}>{u.role === "client_admin" ? "Org Admin" : u.role === "master_admin" ? "Master Admin" : "Member"}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={u.status === "active" ? "bg-lime-100 text-lime-700 border-0 text-[10px]" : u.status === "invited" ? "bg-amber-100 text-amber-700 border-0 text-[10px]" : "bg-red-100 text-red-600 border-0 text-[10px]"}>{u.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">{u.joined_at ? new Date(u.joined_at).toLocaleDateString() : "Pending"}</td>
                      <td className="px-4 py-3 text-right">
                        {u.role !== "master_admin" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1.5 rounded hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600" data-testid={`user-actions-${u.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              {/* Role management */}
                              {u.status === "active" && u.role === "client_member" && (
                                <DropdownMenuItem onClick={async () => { try { await api.put(`/admin/users/${u.id}/role`, { role: "client_admin" }); toast.success("Promoted to Org Admin"); loadClientDetail(selectedClient); } catch { toast.error("Failed"); } }}>
                                  <UserPlus className="w-3.5 h-3.5 mr-2" /> Promote to Org Admin
                                </DropdownMenuItem>
                              )}
                              {u.status === "active" && u.role === "client_admin" && (
                                <DropdownMenuItem onClick={async () => { try { await api.put(`/admin/users/${u.id}/role`, { role: "client_member" }); toast.success("Demoted to Member"); loadClientDetail(selectedClient); } catch { toast.error("Failed"); } }}>
                                  <UserX className="w-3.5 h-3.5 mr-2" /> Demote to Member
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {/* Status management */}
                              {u.status === "active" && (
                                <DropdownMenuItem onClick={async () => { try { await api.put(`/admin/users/${u.id}/suspend`); toast.success("User suspended"); loadClientDetail(selectedClient); } catch(e) { toast.error(e.response?.data?.detail || "Failed"); } }}>
                                  <UserX className="w-3.5 h-3.5 mr-2 text-amber-600" /> Suspend User
                                </DropdownMenuItem>
                              )}
                              {u.status === "suspended" && (
                                <DropdownMenuItem onClick={async () => { try { await api.put(`/admin/users/${u.id}/reactivate`); toast.success("User reactivated"); loadClientDetail(selectedClient); } catch { toast.error("Failed"); } }}>
                                  <Check className="w-3.5 h-3.5 mr-2 text-lime-600" /> Reactivate User
                                </DropdownMenuItem>
                              )}
                              {u.status === "invited" && (
                                <DropdownMenuItem onClick={async () => { try { await api.post(`/admin/users/${u.id}/resend-invite`); toast.success("Invite resent"); } catch(e) { toast.error(e.response?.data?.detail || "Failed"); } }}>
                                  <MailPlus className="w-3.5 h-3.5 mr-2 text-blue-600" /> Resend Invite
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {/* Password & account */}
                              {u.status === "active" && (
                                <DropdownMenuItem onClick={async () => { try { await api.post(`/admin/users/${u.id}/reset-password`); toast.success(`Password reset sent to ${u.email}`); } catch { toast.error("Failed"); } }}>
                                  <KeyRound className="w-3.5 h-3.5 mr-2" /> Send Password Reset
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {/* Destructive */}
                              <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={async () => {
                                if (!window.confirm(`Permanently delete ${u.full_name || u.email}? This cannot be undone.`)) return;
                                try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); loadClientDetail(selectedClient); } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
                              }}>
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Permanently
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-6">
            <h3 className="font-heading text-slate-900 mb-4">Role Searches ({clientDetail.searches?.length})</h3>
            <div className="space-y-2">{clientDetail.searches?.slice(0, 10).map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm text-slate-900 truncate flex-1">{s.title}</span><StatusBadge status={s.status} />
              </div>
            ))}</div>
            <Separator className="my-4" />
            <h3 className="font-heading text-slate-900 mb-4">Work Orders ({clientDetail.work_orders?.length})</h3>
            <div className="space-y-2">{clientDetail.work_orders?.slice(0, 10).map(wo => (
              <div key={wo.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm text-slate-900 truncate flex-1">{wo.title}</span>
                <div className="flex gap-1"><StatusBadge status={wo.status} />{wo.is_demo && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Demo</Badge>}</div>
              </div>
            ))}</div>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-management">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div><h1 className="text-2xl font-heading text-slate-900">Client Management</h1><p className="text-sm text-slate-500 mt-1">Manage client organizations</p></div>
        <div className="flex gap-3">
          <DemoInviteModal api={api} onSuccess={loadClients} />
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button data-testid="new-client-btn" className="bg-evergreen-950 hover:bg-evergreen-900 text-white font-accent"><Plus className="w-4 h-4 mr-1" /> New Client</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle className="font-heading">New Client Organization</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Organization Name</Label><Input data-testid="new-client-name" value={newClient.name} onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Suppression Names (comma-separated)</Label><Input value={newClient.suppression_names} onChange={e => setNewClient(p => ({ ...p, suppression_names: e.target.value }))} placeholder="Novo Nordisk, NNI" /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Suppression Domains (comma-separated)</Label><Input value={newClient.suppression_domains} onChange={e => setNewClient(p => ({ ...p, suppression_domains: e.target.value }))} placeholder="novonordisk.com" /></div>
                <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Primary Admin Email</Label><Input data-testid="new-client-email" value={newClient.admin_email} onChange={e => setNewClient(p => ({ ...p, admin_email: e.target.value }))} /></div>
                <Button data-testid="create-client-btn" onClick={handleCreate} disabled={creating} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white">{creating ? "Creating..." : "Create Client"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div> : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left">
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Organization</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Status</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Tier</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Users</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Searches</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">WOs</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Created</th>
              <th className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">Actions</th>
            </tr></thead>
            <tbody>
              {clients.map(c => {
                const isDemoOrg = c.contract_status === "demo" || c.demo_mode;
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3"><span className="font-medium text-slate-900 cursor-pointer hover:text-lime-700" onClick={() => loadClientDetail(c.id)}>{c.name}</span></td>
                    <td className="px-4 py-3"><ContractBadge org={c} /></td>
                    <td className="px-4 py-3"><Badge variant="secondary" className="text-xs">{c.contract_tier || "—"}</Badge></td>
                    <td className="px-4 py-3 text-slate-600">{c.user_count}</td>
                    <td className="px-4 py-3 text-slate-600">{c.search_count ?? 0}</td>
                    <td className="px-4 py-3 text-slate-600">{c.wo_count ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className="text-xs" onClick={() => loadClientDetail(c.id)}>View</Button>
                        {isDemoOrg && <UpgradeModal org={c} api={api} onSuccess={loadClients} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
